import Button from 'components/Button';
import { useCheckout } from 'features/events/checkout/context';
import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import AuthenticationFlow from 'legacy/auth/components/authentication-flow/AuthenticationFlow';
import { CheckoutSteps } from 'features/events/checkout/types';
import { useTranslation } from 'react-i18next';
import {
  isMaxUsageError,
  shouldDisableCheckoutForError
} from 'features/events/checkout/helpers/checkoutErrorHelpers';

import { capitalize } from 'lodash';
import { useViewEvent } from 'features/events/context/ViewEvent';

const ConfirmOrderButton = () => {
  const { t } = useTranslation(['checkout', 'common']);
  const { isAuthenticated } = useAuthenticationStatus();
  const { event } = useViewEvent();
  const {
    checkoutSession: {
      checkoutError,
      confirmCheckout,
      isLoadingCheckout,
      checkoutSession,
      isCheckoutDisabled
    },
    waivers,
    paymentMethod,
    onAuthenticationSuccess,
    onSuccess,
    goToStep
  } = useCheckout();

  const isTicketBasketEmpty =
    Object.keys(checkoutSession?.ticketBasket || {}).length === 0;

  // Block the user from submitting if
  // - Checkout is busy.
  // - User has selected paid tickets AND checkout session has not been assigned a card yet.
  // - User has selected a payment method other than card.
  // - There's a non-maxUsage error
  const isDisabled =
    shouldDisableCheckoutForError(checkoutError) ||
    Boolean(isCheckoutDisabled) ||
    isLoadingCheckout ||
    (checkoutSession?.total > 0 &&
      !checkoutSession?.card &&
      !paymentMethod.selectedBuyNowPayLater.value) ||
    isTicketBasketEmpty;

  const onConfirm = () => {
    if (waivers.data.length > 0) {
      goToStep(CheckoutSteps.Waivers);
    } else if (event.prePurchaseFormId) {
      goToStep(CheckoutSteps.PrePurchaseQuestions);
    } else {
      confirmCheckout({ allWaiversAccepted: waivers.allWaiversAccepted }).then(
        () => {
          onSuccess();
          goToStep(CheckoutSteps.Success);
        }
      );
    }
  };

  const getButtonText = () => {
    if (waivers.data.length > 0) {
      return t('checkout:continueToWaivers');
    }

    if (event.prePurchaseFormId) {
      return t('checkout:continueToPrePurchaseQuestions');
    }

    if (checkoutSession?.total === 0) {
      return t('checkout:confirmAndRsvp');
    }

    if (paymentMethod.selectedBuyNowPayLater.value) {
      return t('checkout:continueTo', {
        paymentMethod: capitalize(paymentMethod.selectedBuyNowPayLater.value)
      });
    }

    return t('checkout:confirmAndPay');
  };

  const buttonText = getButtonText();

  if (!isAuthenticated()) {
    return (
      <AuthenticationFlow onCompleteAuthentication={onAuthenticationSuccess}>
        {({ startAuthenticationFlow }) => (
          <>
            <Button
              variant="primaryDark"
              size="large"
              className="w-full mt-0!"
              loading={isLoadingCheckout}
              onClick={startAuthenticationFlow}
            >
              {t('common:buttons.continue')}
            </Button>
          </>
        )}
      </AuthenticationFlow>
    );
  }

  return (
    <>
      {checkoutError?.message && (
        <div
          className={`font-normal text-base text-center pb-4 max-h-[60px] overflow-y-auto ${
            isMaxUsageError(checkoutError) ? 'text-orange-600' : 'text-error'
          }`}
        >
          {checkoutError.message}
        </div>
      )}
      <Button
        variant="primaryDark"
        size="large"
        className="w-full mt-0!"
        loading={isLoadingCheckout}
        onClick={onConfirm}
        disabled={isDisabled}
      >
        {buttonText}
      </Button>
    </>
  );
};

export default ConfirmOrderButton;
